import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import {
  ChevronDown,
  LogOut,
  Menu as MenuIcon,
  Search,
  X,
} from 'lucide-react';
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { ThemeSwitcher } from '@/theme/ThemeSwitcher';
import { cn } from '@/utils/cls';
import { fmtPhone } from '@/utils/format';

import { navForRoles, type NavItem } from './navigation';

/**
 * Top-level chrome: persistent left sidebar on desktop (≥ lg), slide-in
 * drawer on tablet/mobile, sticky topbar with search + theme switcher +
 * user menu on every breakpoint.
 *
 * Pages render through the React Router <Outlet />. Width is capped at
 * a sensible max so reports don't stretch into illegibility on 4K
 * monitors.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { me } = useAuth();
  const items = useMemo(() => navForRoles(me?.roles ?? []), [me]);

  return (
    <div className="min-h-screen bg-surface-2 flex">
      {/* ── Desktop sidebar ─────────────────────────────────────── */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 border-r border-line bg-surface flex-col">
        <BrandHeader />
        <SidebarNav items={items} />
        <SidebarFooter />
      </aside>

      {/* ── Mobile / tablet drawer ─────────────────────────────── */}
      <Transition show={drawerOpen} as={Fragment}>
        <Dialog onClose={() => setDrawerOpen(false)} className="relative z-40 lg:hidden">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" aria-hidden="true" />
          </TransitionChild>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <DialogPanel className="fixed inset-y-0 left-0 w-72 bg-surface flex flex-col border-r border-line">
              <BrandHeader onClose={() => setDrawerOpen(false)} />
              <SidebarNav items={items} onNavigate={() => setDrawerOpen(false)} />
              <SidebarFooter />
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      {/* ── Main column ─────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar onMenu={() => setDrawerOpen(true)} />
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/* ───────────────────── building blocks ────────────────────── */

function BrandHeader({ onClose }: { onClose?: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-line">
      <Link to="/" className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-brand-500 grid place-items-center text-white font-bold">
          V
        </div>
        <div>
          <div className="font-semibold text-ink leading-tight">New Vishwas Electronics</div>
          <div className="text-[11px] text-ink-soft leading-tight">Owner panel</div>
        </div>
      </Link>
      {onClose && (
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-ink-soft hover:bg-surface-2"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function SidebarNav({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  // Group items by `group` (undefined items appear at the top, ungrouped).
  const groups = useMemo(() => {
    const map = new Map<string | undefined, NavItem[]>();
    for (const item of items) {
      const key = item.group;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries());
  }, [items]);

  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
      {groups.map(([group, list]) => (
        <div key={group ?? 'top'}>
          {group && (
            <div className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-soft">
              {group}
            </div>
          )}
          <ul className="space-y-0.5">
            {list.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-brand-500/10 text-brand-700'
                          : 'text-ink-soft hover:text-ink hover:bg-surface-2',
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function SidebarFooter() {
  return (
    <div className="px-5 py-3 border-t border-line text-[11px] text-ink-soft">
      v1.0 · synced with mobile
    </div>
  );
}

function Topbar({ onMenu }: { onMenu: () => void }) {
  return (
    <header className="sticky top-0 z-30 bg-surface/85 backdrop-blur border-b border-line">
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-3">
        <button
          onClick={onMenu}
          className="lg:hidden p-2 -ml-2 rounded-lg text-ink-soft hover:bg-surface-2"
          aria-label="Open menu"
        >
          <MenuIcon className="h-5 w-5" />
        </button>

        <PageBreadcrumb />

        <div className="ml-auto flex items-center gap-2">
          <SearchPill />
          <ThemeSwitcher />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

/** Tiny breadcrumb derived from the URL — saves us threading title state through every page. */
function PageBreadcrumb() {
  const { pathname } = useLocation();
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) {
    return <div className="font-semibold text-ink hidden sm:block">Dashboard</div>;
  }
  return (
    <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1.5 text-sm text-ink-soft min-w-0">
      <Link to="/" className="hover:text-ink">
        Home
      </Link>
      {parts.map((p, idx) => {
        const path = '/' + parts.slice(0, idx + 1).join('/');
        const isLast = idx === parts.length - 1;
        return (
          <Fragment key={path}>
            <span>/</span>
            {isLast ? (
              <span className="text-ink font-medium capitalize truncate">
                {p.replace(/-/g, ' ')}
              </span>
            ) : (
              <Link to={path} className="hover:text-ink capitalize">
                {p.replace(/-/g, ' ')}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}

/**
 * Stub search box — wires straight to the customers/products/bills
 * search endpoints once we drop in a command palette. Keyboard focus
 * via `/` is already a familiar pattern from GitHub etc.
 */
function SearchPill() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) navigate(`/products?search=${encodeURIComponent(q.trim())}`);
      }}
      className="hidden md:flex items-center gap-2 h-9 px-3 rounded-xl bg-surface-2 border border-line min-w-[260px]"
    >
      <Search className="h-4 w-4 text-ink-soft" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search products, bills, customers…"
        className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-ink-soft/60"
      />
    </form>
  );
}

function UserMenu() {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (!me) return null;
  const initials = me.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  const role = me.is_owner
    ? 'Owner'
    : me.roles.includes('manager')
      ? 'Manager'
      : me.roles.includes('accountant')
        ? 'CA'
        : me.roles[0] ?? 'Staff';
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-surface-2"
      >
        <span className="h-7 w-7 rounded-full bg-brand-500 text-white grid place-items-center text-xs font-bold">
          {initials || 'U'}
        </span>
        <span className="hidden sm:flex items-center gap-1 text-sm">
          <span className="font-medium">{me.name}</span>
          <ChevronDown className="h-4 w-4 text-ink-soft" />
        </span>
      </button>
      {open && (
        <Dropdown onClose={() => setOpen(false)}>
          <div className="px-3 py-2 border-b border-line">
            <div className="text-sm font-semibold text-ink truncate">{me.name}</div>
            <div className="text-xs text-ink-soft">{fmtPhone(me.phone)}</div>
            <div className="text-xs text-brand-700 mt-0.5">{role}</div>
          </div>
          <ul className="py-1">
            <li>
              <Link
                to="/settings/profile"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-sm text-ink hover:bg-surface-2"
              >
                Business profile
              </Link>
            </li>
            <li>
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-sm text-ink hover:bg-surface-2"
              >
                Settings
              </Link>
            </li>
          </ul>
          <div className="border-t border-line p-1">
            <Button
              variant="ghost"
              size="sm"
              block
              onClick={() => {
                setOpen(false);
                void logout();
              }}
              iconLeft={<LogOut className="h-4 w-4" />}
            >
              Sign out
            </Button>
          </div>
        </Dropdown>
      )}
    </div>
  );
}

function Dropdown({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <>
      <button
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-40 cursor-default"
      />
      <div className="absolute right-0 z-50 mt-2 w-60 card p-0 overflow-hidden animate-slide-up">
        {children}
      </div>
    </>
  );
}
